import React, { useCallback, useEffect, useState } from "react";
import { blogAdminService, BlogTopic } from "../../services/blogAdminService";

// Manage the automated-blog topic queue: list, add, plan (LLM), delete.
// Mirrors NewsletterTopicsPanel. See .claude/tasks/blog-automation.md.

interface Props {
  notify: (msg: string, type?: "success" | "error") => void;
}

const statusColor: Record<string, string> = {
  pending: "text-amber-400",
  used: "text-green-400",
  failed: "text-red-400",
};

const BlogTopicsPanel: React.FC<Props> = ({ notify }) => {
  const [topics, setTopics] = useState<BlogTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [keywords, setKeywords] = useState("");
  const [category, setCategory] = useState("Article");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTopics(await blogAdminService.listTopics());
    } catch {
      notify("Failed to load topics", "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const addTopic = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await blogAdminService.createTopic({
        title: title.trim(),
        target_keywords: keywords.trim() || null,
        category,
      });
      setTitle("");
      setKeywords("");
      notify("Topic added", "success");
      await load();
    } catch {
      notify("Failed to add topic", "error");
    } finally {
      setBusy(false);
    }
  };

  const planTopics = async () => {
    setBusy(true);
    try {
      const added = await blogAdminService.planTopics();
      notify(`Planned ${added} topic(s)`, "success");
      await load();
    } catch {
      notify("Failed to plan topics", "error");
    } finally {
      setBusy(false);
    }
  };

  const removeTopic = async (id: string) => {
    try {
      await blogAdminService.deleteTopic(id);
      setTopics((t) => t.filter((x) => x.id !== id));
    } catch {
      notify("Failed to delete topic", "error");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Topic queue</h3>
        <button
          onClick={planTopics}
          disabled={busy}
          className="px-3 py-1.5 rounded-md bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold disabled:opacity-50"
        >
          Plan with AI
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-2">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="New topic title"
          className="bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-white"
        />
        <input
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="keywords, comma separated"
          className="bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-white"
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-sm text-white"
        >
          <option>Article</option>
          <option>Resource</option>
          <option>Update</option>
        </select>
        <button
          onClick={addTopic}
          disabled={busy || !title.trim()}
          className="px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold disabled:opacity-50"
        >
          Add
        </button>
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm">Loading…</p>
      ) : topics.length === 0 ? (
        <p className="text-gray-400 text-sm">No topics yet. Add one or plan with AI.</p>
      ) : (
        <ul className="divide-y divide-gray-800 border border-gray-800 rounded-md">
          {topics.map((t) => (
            <li key={t.id} className="flex items-center gap-3 px-3 py-2">
              <span className="text-xs text-gray-500 w-6">{t.position}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{t.title}</p>
                <p className="text-xs text-gray-500 truncate">
                  {t.category}
                  {t.target_keywords ? ` · ${t.target_keywords}` : ""}
                  {t.attempts > 0 ? ` · ${t.attempts} attempt(s)` : ""}
                </p>
              </div>
              <span className={`text-xs font-semibold ${statusColor[t.status] ?? "text-gray-400"}`}>
                {t.status}
              </span>
              <button
                onClick={() => removeTopic(t.id)}
                className="text-xs text-red-400 hover:text-red-300"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default BlogTopicsPanel;
